/**
 * Session 7 Phase 1 — Tenants list.
 * Filter chips, full-text search, pagination, row-click → detail.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";

const STATUS_TABS = [
  { k: "",                    label: "Alle" },
  { k: "active",              label: "Aktiv" },
  { k: "pending_approval",    label: "Pending" },
  { k: "suspended",           label: "Suspendiert" },
  { k: "archived",            label: "Archiviert" },
];

function statusBadge(status) {
  const map = {
    active: "secondary", pending_approval: "default",
    suspended: "destructive", archived: "outline",
  };
  return <Badge variant={map[status] || "secondary"}>{status}</Badge>;
}

export default function OperatorTenants() {
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 25 });
  const [loading, setLoading] = useState(false);

  const reload = () => {
    setLoading(true);
    const params = new URLSearchParams({
      page: page.toString(), page_size: "25", sort: "created_at_desc",
    });
    if (status) params.append("status", status);
    if (search) params.append("search", search);
    api.get(`/operator/tenants?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [status, page]);   // eslint-disable-line

  const totalPages = Math.max(1, Math.ceil(data.total / (data.page_size || 25)));

  return (
    <div className="space-y-6" data-testid="operator-tenants">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Tenants</h1>
        <p className="text-sm text-muted-foreground">
          Alle Mandanten der Plattform. Aktionen ändern Status; Inhalte
          bleiben unangetastet.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((t) => (
          <Button
            key={t.k || "all"}
            size="sm"
            variant={status === t.k ? "default" : "outline"}
            data-testid={`tenants-filter-${t.k || "all"}`}
            onClick={() => { setPage(1); setStatus(t.k); }}
          >
            {t.label}
          </Button>
        ))}
        <div className="flex-1" />
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); reload(); }}
          className="flex gap-2"
        >
          <Input
            placeholder="Suche Slug / Name …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
            data-testid="tenants-search"
          />
          <Button size="sm" type="submit" data-testid="tenants-search-submit">
            Suchen
          </Button>
        </form>
      </div>

      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Firma / Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead className="text-right">Nutzer</TableHead>
                <TableHead className="text-right">Aktive Kampagnen</TableHead>
                <TableHead>Erstellt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow data-testid="tenants-loading">
                  <TableCell colSpan={7} className="text-sm text-muted-foreground">
                    Lade …
                  </TableCell>
                </TableRow>
              )}
              {!loading && data.items.length === 0 && (
                <TableRow data-testid="tenants-empty">
                  <TableCell colSpan={7} className="text-sm text-muted-foreground">
                    Keine Tenants gefunden.
                  </TableCell>
                </TableRow>
              )}
              {!loading && data.items.map((t) => (
                <TableRow key={t.id} data-testid={`tenant-row-${t.slug}`}>
                  <TableCell>
                    <Link
                      to={`/operator/tenants/${t.id}`}
                      className="hover:underline"
                    >
                      <div className="text-sm font-medium">{t.name}</div>
                      <code className="text-xs font-mono text-muted-foreground">{t.slug}</code>
                    </Link>
                  </TableCell>
                  <TableCell>{statusBadge(t.status)}</TableCell>
                  <TableCell><Badge variant="outline">{t.plan || "free"}</Badge></TableCell>
                  <TableCell className="text-xs">{t.owner_email_masked}</TableCell>
                  <TableCell className="text-right">{t.users_count}</TableCell>
                  <TableCell className="text-right">{t.active_campaigns_count}</TableCell>
                  <TableCell className="text-xs">
                    {t.created_at?.slice(0, 10) || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {data.total} Ergebnisse · Seite {page}/{totalPages}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="tenants-prev">Zurück</Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="tenants-next">Weiter</Button>
        </div>
      </div>
    </div>
  );
}
